/**
 * 绽光 · 孕姿数据
 * 场景为主轴（scenes[] 可多选）+ couple 辅筛
 */

export const CATEGORIES = [
  { id: 'all', label: '全部' },
  { id: 'indoor', label: '室内居家' },
  { id: 'window', label: '窗边光影' },
  { id: 'cafe', label: '咖啡馆' },
  { id: 'outdoor', label: '户外自然' },
  { id: 'bamboo', label: '竹林夹道' },
  { id: 'bench', label: '长椅公园' },
  { id: 'stairs', label: '楼梯台阶' },
  { id: 'flower', label: '花墙绿植' },
  { id: 'balcony', label: '阳台日落' },
  { id: 'mirror', label: '镜面反射' },
  { id: 'lake', label: '湖边水岸' },
  { id: 'town', label: '古镇巷弄' },
  { id: 'street', label: '街边城市' },
  { id: 'couple', label: '双人甜蜜' },
  { id: 'closeup', label: '特写细节' },
];

export const SCENE_GUIDES = {
  indoor: '选浅色墙与干净床品；落地灯或窗光侧打，避免顶灯硬影。',
  window: '大窗侧光最佳；纱帘柔化光线，脸部可略补柔光。',
  cafe: '靠窗座位光最好；前景可虚化杯子，避开杂乱菜单牌。',
  outdoor: '上午或傍晚柔光；避开正午顶光，裙摆注意防脏。',
  bamboo: '竹林夹道选安全路肩停车再拍；仰拍竹梢透光，脚下防滑，会车时先回车。',
  bench: '选干净长椅与简洁背景；侧坐更显轮廓，注意起身慢。',
  stairs: '选干净低缓楼梯；注意防滑，旁人搀扶，不要站边缘。',
  flower: '人少时段拍；花墙做背景别挡脸，注意花粉过敏。',
  balcony: '日落前后 30 分钟；注意安全护栏，逆光记得补脸。',
  mirror: '擦净镜面；自己别入镜除非想拍合影，注意构图裁切。',
  lake: '逆光或侧逆光勾轮廓；注意栏杆安全，风大固定发丝。只停正规观景停车位。',
  town: '古镇石板雨天湿滑；白墙巷口人少时拍，少站桥心人流，短走即回。',
  street: '浅色墙面或安静巷口；人少时段拍，注意来往车辆。',
  couple: '先对焦女主眼睛；他的手轻托不按压，身高差可屈膝对齐。',
  closeup: '浅景深、干净指甲；可加婚戒或小物增加故事感。',
};

export const SCENE_LABELS = Object.fromEntries(
  CATEGORIES.filter((c) => c.id !== 'all').map((c) => [c.id, c.label])
);

export const PREP_ITEMS = [
  {
    id: 'clothes',
    title: '服装',
    items: [
      '贴身弹性面料，突出孕肚曲线',
      '纯色优于复杂花纹，浅色更显柔光',
      '备一件薄披肩 / 纱质外套做层次',
      '户外备防脏垫布，坐草地/长椅用',
    ],
  },
  {
    id: 'light',
    title: '光线',
    items: [
      '优先大窗侧光，上午 9–11 点或傍晚柔光',
      '避免正午顶光在眼下留重影',
      '逆光时用反光板或手机补脸',
      '室内可开落地灯做暖调辅光',
    ],
  },
  {
    id: 'props',
    title: '道具',
    items: [
      '干花束、薄纱、摇椅、靠垫',
      '宝宝用品（小鞋、超声照片）做特写',
      '情侣戒指 / 牵手细节',
      '干净浅色背景墙或床品',
    ],
  },
  {
    id: 'talk',
    title: '沟通',
    items: [
      '没事就拍 3 张：选「短拍快选」即可，不必一天拍完',
      '每组摆姿不超过 1–2 分钟',
      '准备水与休息椅，随时暂停',
      '多用具体口令，少说「别动、再挺一点」',
    ],
  },
];

export const REFS = [
  {
    id: 'ref-side',
    title: '侧身轮廓',
    caption: '窗边侧 45°，一手托肚，光勾勒孕肚弧线',
    src: 'refs/ref-side.png',
    tone: 'mist',
  },
  {
    id: 'ref-couple',
    title: '双人依偎',
    caption: '他从身后环抱，下巴轻靠肩窝，画面安静',
    src: 'refs/ref-couple.png',
    tone: 'blush',
  },
  {
    id: 'ref-window',
    title: '窗光凝望',
    caption: '面对窗户，侧脸看向光，情绪留白',
    src: 'refs/ref-window.png',
    tone: 'dawn',
  },
  {
    id: 'ref-detail',
    title: '双手托肚',
    caption: '俯拍或平拍双手交叠，聚焦触感',
    src: 'refs/ref-detail.png',
    tone: 'soft',
  },
];

function pose(p) {
  return {
    difficulty: 1,
    duration: '1 分钟',
    tags: [],
    tips: [],
    ...p,
    image: p.image || `poses/${p.id}.png`,
    svg: p.svg || p.id,
  };
}

export const POSES = [
  // ——— 保留 16 旧姿（重标 scenes）———
  pose({
    id: 'solo-profile',
    name: '经典侧身',
    scenes: ['outdoor', 'indoor'],
    couple: false,
    tags: ['侧身', '站姿', '轮廓'],
    summary: '最经典的孕肚侧影，线条干净，适合开场定调。',
    steps: [
      '侧身对镜头约 70°–90°，重心落在离镜头较远的那只脚',
      '远离镜头的手轻托肚底，靠近镜头的手自然垂落或轻扶上腹',
      '下巴微收，目光看向斜前方或望向窗外，肩膀放松下沉',
    ],
    tips: ['骨盆轻微前倾，肚子更有弧度', '不要刻意挺胸屏气', '纱裙或长裙让腿部线条更长'],
    camera: '腰部以上或全身，镜头略低于肚脐',
  }),
  pose({
    id: 'solo-front-hold',
    name: '正托孕肚',
    scenes: ['indoor', 'outdoor'],
    couple: false,
    tags: ['正面', '双手', '温馨'],
    summary: '双手环抱孕肚，表情柔和，适合半身肖像。',
    steps: [
      '正面或微侧 15° 站立，双脚与肩同宽，膝盖放松',
      '双手交叠轻托肚前，手指自然打开不要握拳',
      '头微侧，嘴角轻笑，眼睛看镜头或看向肚上的手',
    ],
    tips: ['手肘微向外打开', '露出锁骨与脖颈更显优雅', '衣服与背景拉开对比'],
    camera: '半身，焦点在眼睛与双手',
  }),
  pose({
    id: 'solo-back-glance',
    name: '回眸侧影',
    scenes: ['outdoor', 'street', 'town'],
    couple: false,
    difficulty: 2,
    duration: '1–2 分钟',
    tags: ['回眸', '侧身', '情绪'],
    summary: '背对镜头微侧，回眸看向相机，氛围感强。',
    steps: [
      '几乎背对镜头，身体侧转约 30°，露出孕肚侧弧',
      '一手轻扶肚侧，另一手可轻拉发丝或扶墙',
      '头部回转看镜头，目光柔和，不要耸肩',
    ],
    tips: ['发丝落在一侧肩上更有故事感', '腰背不要过度反弓', '适合长裙与薄纱披肩'],
    camera: '全身或 3/4 身，背景干净留白',
  }),
  pose({
    id: 'solo-lean-wall',
    name: '靠墙休息',
    scenes: ['indoor', 'street', 'town'],
    couple: false,
    tags: ['靠墙', '轻松', '自然'],
    summary: '靠墙或门框，姿态放松，像日常片刻。',
    steps: [
      '一侧肩背轻靠墙，对侧髋微向外，形成 S 曲线',
      '靠近墙的手可扶墙或垂落，另一手托肚',
      '头靠墙或微抬，表情像在歇口气',
    ],
    tips: ['墙面选浅色或有纹理', '脚尖略外展更松弛', '适合家居与便装'],
    camera: '竖构图，留出墙面负空间',
  }),
  pose({
    id: 'solo-flower',
    name: '花束侧拥',
    scenes: ['indoor', 'outdoor'],
    couple: false,
    difficulty: 2,
    duration: '1–2 分钟',
    tags: ['道具', '花束', '浪漫'],
    summary: '单手持干花或鲜切花，与孕肚形成对角构图。',
    steps: [
      '侧身站立，花束抱在胸口与孕肚之间的斜线位置',
      '另一手轻托肚底，花茎不要挡住房子中心',
      '脸微侧看花或看镜头，肩线放松',
    ],
    tips: ['花色与服装呼应', '花束高度约在胸口', '可让花瓣轻微虚化做前景'],
    camera: '半身，浅景深突出人与花',
  }),
  pose({
    id: 'couple-behind',
    name: '身后环抱',
    scenes: ['couple', 'indoor', 'outdoor'],
    couple: true,
    tags: ['双人', '环抱', '甜蜜'],
    summary: '他从身后轻轻环住孕肚，经典夫妻孕照。',
    steps: [
      '她侧身或微侧，他站在身后略偏一侧',
      '他的双手从两侧轻托孕肚下方，掌心贴合不要用力',
      '他的下巴可靠在她肩窝，两人看向同一方向或对视',
    ],
    tips: ['身高差大时他可微屈膝', '手在肚下沿更显呵护', '先对焦她的眼睛'],
    camera: '半身横竖皆可，注意两人脸部都进景',
  }),
  pose({
    id: 'couple-forehead',
    name: '额抵额',
    scenes: ['couple', 'indoor'],
    couple: true,
    difficulty: 2,
    tags: ['双人', '亲密', '特写'],
    summary: '两人额头相抵，双手交叠在孕肚上。',
    steps: [
      '面对面站立，额头轻轻相抵，鼻尖可轻触',
      '双手交叠放在孕肚中央，呼吸同步',
      '闭眼或半睁眼，表情安静',
    ],
    tips: ['侧光最能勾出两人轮廓', '镜头可略抬高', '适合黑白后期'],
    camera: '近景或特写，焦点在额头与眼睛',
  }),
  pose({
    id: 'couple-kiss-belly',
    name: '轻吻孕肚',
    scenes: ['couple', 'indoor'],
    couple: true,
    difficulty: 2,
    duration: '1–2 分钟',
    tags: ['双人', '亲吻', '温情'],
    summary: '他单膝或弯腰轻吻孕肚，她低头微笑。',
    steps: [
      '她站立，一手扶他肩，一手轻抚自己的肚',
      '他侧跪或弯腰，嘴唇轻触肚侧，双手扶住她腰侧',
      '她低头看他，或看向镜头，表情温柔',
    ],
    tips: ['他的膝盖垫软垫', '构图留出他的侧脸', '可连拍捕捉微笑'],
    camera: '竖构图，主体偏中上',
  }),
  pose({
    id: 'couple-walk',
    name: '并肩慢走',
    scenes: ['couple', 'outdoor', 'street'],
    couple: true,
    duration: '2 分钟',
    tags: ['双人', '行走', '自然'],
    summary: '挽手慢走，抓拍途中自然互动。',
    steps: [
      '并排走向镜头斜侧 30°，步伐放慢',
      '内侧手相牵或他托住她手肘',
      '边走边交谈，可偶尔对视或看前方',
    ],
    tips: ['用连拍选步伐优美的一帧', '裙摆与风很加分', '地面干净少杂物'],
    camera: '全身，快门略提高防模糊',
  }),
  pose({
    id: 'seated-chair',
    name: '侧坐摇椅',
    scenes: ['indoor'],
    couple: false,
    duration: '2 分钟',
    tags: ['坐姿', '摇椅', '居家'],
    summary: '侧坐于椅，一手托肚一手扶扶手，最省体力。',
    steps: [
      '侧身坐稳，双脚自然落地或一脚轻踮',
      '一手托肚，一手搭扶手或扶椅背',
      '背微靠，肩放松，头可微侧靠椅背',
    ],
    tips: ['椅子高度适中', '腰后加薄垫更舒适', '适合补拍与休息穿插'],
    camera: '半身或全身，注意椅脚不要裁切尴尬',
  }),
  pose({
    id: 'seated-floor',
    name: '地面倚坐',
    scenes: ['indoor', 'outdoor'],
    couple: false,
    difficulty: 2,
    duration: '1–2 分钟',
    tags: ['坐姿', '地面', '柔美'],
    summary: '坐于地毯或床沿，裙摆铺开，画面柔软。',
    steps: [
      '屈膝侧坐，孕肚朝向镜头一侧，支撑手在身后或身侧',
      '另一手轻托肚或放在膝上',
      '上身微前倾或直立，目光柔和向下或平视',
    ],
    tips: ['起身要慢，旁人搀扶', '裙摆提前铺好', '孕晚期慎选，以舒适为准'],
    camera: '略俯拍，突出裙摆与孕肚',
  }),
  pose({
    id: 'seated-couple-bed',
    name: '床沿依偎',
    scenes: ['indoor', 'couple'],
    couple: true,
    duration: '2 分钟',
    tags: ['坐姿', '双人', '居家'],
    summary: '两人坐床沿，他环住她，适合居家温馨风。',
    steps: [
      '她坐床沿，脚可触地；他坐在身侧略后',
      '他一手环腰或托肚，她头可靠他肩',
      '两人看同一方向，或她看镜头他看她',
    ],
    tips: ['床单褶皱提前整理', '窗光从侧面进来最美', '可加一杯温水进画面'],
    camera: '半身横构图',
  }),
  pose({
    id: 'window-gaze',
    name: '望窗沉思',
    scenes: ['window'],
    couple: false,
    tags: ['窗光', '侧脸', '情绪'],
    summary: '面对窗户，侧脸沐浴在光里，情绪安静。',
    steps: [
      '站在窗前约 0.5–1 米，身体微侧，脸朝向光源',
      '一手轻扶窗框或窗帘，一手托肚',
      '眼睛看向窗外远处，不要眯眼看太阳',
    ],
    tips: ['纱帘柔化光线', '曝光参考脸部', '可拍剪影版本做对比'],
    camera: '侧脸半身，光比控制在柔和范围',
  }),
  pose({
    id: 'window-silhouette',
    name: '窗边剪影',
    scenes: ['window'],
    couple: false,
    difficulty: 2,
    tags: ['窗光', '剪影', '艺术'],
    summary: '逆光剪影突出孕肚轮廓，极简有力量。',
    steps: [
      '完全侧身站在明亮窗前，与窗保持一点距离',
      '双手可交叠托肚，轮廓清晰即可',
      '抬头或平视，做出干净的头颈线条',
    ],
    tips: ['对窗外测光，人物压成剪影', '背景不要有杂乱线条', '头发束起轮廓更干净'],
    camera: '全身侧影，横竖皆可',
  }),
  pose({
    id: 'closeup-hands',
    name: '双手托肚特写',
    scenes: ['closeup'],
    couple: false,
    tags: ['特写', '双手', '细节'],
    summary: '只拍手与孕肚，温暖而克制。',
    steps: [
      '双手交叠轻放在孕肚最饱满处',
      '手指自然舒展，可露出婚戒',
      '上身微前倾，让肚与手充满画面',
    ],
    tips: ['指甲保持干净自然色', '皮肤涂一点身体乳', '可加丈夫的手叠在上层'],
    camera: '特写，焦点在手指与肚弧',
  }),
  pose({
    id: 'closeup-profile-face',
    name: '母爱侧颜',
    scenes: ['closeup', 'indoor'],
    couple: false,
    tags: ['特写', '侧颜', '情绪'],
    summary: '侧脸低头看向孕肚，捕捉母爱瞬间。',
    steps: [
      '身体微侧，头低看向自己的手或肚子',
      '一手轻贴肚面，肩线放松',
      '表情安静微笑或闭眼感受胎动',
    ],
    tips: ['眉毛睫毛可轻微化妆', '发丝别遮住眼睛', '适合拍胎动真实反应'],
    camera: '头肩特写，浅景深',
  }),

  // ——— 新增约 20 姿 ———
  pose({
    id: 'indoor-sofa-hold',
    name: '沙发侧坐托肚',
    scenes: ['indoor'],
    couple: false,
    duration: '2 分钟',
    tags: ['沙发', '坐姿', '居家'],
    summary: '侧坐沙发，裙摆自然垂落，最日常也好出片。',
    steps: [
      '侧身坐在沙发中段，靠背可轻贴，双脚自然落地',
      '一手托肚底，另一手搭扶手或放在腿侧',
      '头微侧，目光看镜头或看向肚上的手',
    ],
    tips: ['沙发颜色浅、少杂乱靠垫', '起身慢，旁边有人更好', '可加一杯水增加生活感'],
    camera: '半身横构图，略低于视线',
  }),
  pose({
    id: 'indoor-bed-pillow',
    name: '床上靠枕',
    scenes: ['indoor'],
    couple: false,
    duration: '2 分钟',
    tags: ['床品', '靠坐', '柔光'],
    summary: '靠坐床头，枕头垫腰，温柔居家感。',
    steps: [
      '靠坐床头，腰后垫软枕，膝盖微屈',
      '双手轻放孕肚或交叠托住',
      '肩放松，头可靠枕或微抬看镜头',
    ],
    tips: ['床单平整、颜色干净', '避免完全平躺压迫', '窗光从侧面进来最美'],
    camera: '半身，焦点在脸与手',
  }),
  pose({
    id: 'indoor-doorframe',
    name: '门框倚靠',
    scenes: ['indoor'],
    couple: false,
    tags: ['门框', '站姿', '线条'],
    summary: '倚靠门框形成竖线引导，身形更修长。',
    steps: [
      '一侧肩或髋轻贴门框，身体微侧',
      '靠门框的手可扶框，另一手托肚',
      '目光看镜头斜前方，下巴微收',
    ],
    tips: ['门框与墙面简洁', '注意脚下门槛安全', '可留出门洞负空间'],
    camera: '全身竖构图',
  }),
  pose({
    id: 'indoor-couple-sofa',
    name: '双人沙发依偎',
    scenes: ['indoor', 'couple'],
    couple: true,
    duration: '2 分钟',
    tags: ['双人', '沙发', '居家'],
    summary: '同坐沙发，他环住她，日常而亲密。',
    steps: [
      '她侧坐或微侧坐沙发，他坐在身侧略后',
      '他一手环肩或托肚，她可靠他肩',
      '两人看同一方向，或轻声交谈抓拍',
    ],
    tips: ['沙发不要堆太多靠垫', '先对焦她的眼睛', '可连拍抓自然笑'],
    camera: '半身横构图',
  }),
  pose({
    id: 'window-curtain',
    name: '纱帘半身',
    scenes: ['window'],
    couple: false,
    tags: ['纱帘', '柔光', '半身'],
    summary: '半隐在纱帘后，光线朦胧，氛围感强。',
    steps: [
      '站在纱帘内侧或外侧，身体微侧',
      '一手轻拉纱帘边缘，一手托肚',
      '脸朝向进光方向，表情安静',
    ],
    tips: ['纱帘干净无脏渍', '避免脸部过曝', '可拍半剪影'],
    camera: '半身，曝光兼顾脸与窗帘层次',
  }),
  pose({
    id: 'window-sill-sit',
    name: '窗台轻坐',
    scenes: ['window', 'indoor'],
    couple: false,
    difficulty: 2,
    duration: '1–2 分钟',
    tags: ['窗台', '坐姿', '光影'],
    summary: '轻坐宽窗台，侧光勾勒轮廓（需稳固安全）。',
    steps: [
      '确认窗台宽且稳，侧坐或微侧，一脚可轻踮地',
      '一手托肚，一手轻扶窗框',
      '脸朝向光线，目光看窗外或看镜头',
    ],
    tips: ['不安全的窗台绝不拍', '旁人搀扶起身', '裙摆注意别挂住'],
    camera: '半身或全身，侧光为主',
  }),
  pose({
    id: 'outdoor-tree',
    name: '树下侧身',
    scenes: ['outdoor', 'bamboo'],
    couple: false,
    tags: ['树下', '侧身', '自然'],
    summary: '树荫下经典侧影，光斑柔和。',
    steps: [
      '站在树干旁侧身，肚朝向镜头一侧',
      '一手托肚，另一手可轻扶树干',
      '抬头看树叶间隙或平视远方',
    ],
    tips: ['避开斑驳过碎的光斑打脸', '注意脚下不平', '绿叶做前景可虚化'],
    camera: '全身或 3/4 身，竖构图',
  }),
  pose({
    id: 'outdoor-grass-sit',
    name: '草地铺裙',
    scenes: ['outdoor'],
    couple: false,
    difficulty: 2,
    duration: '1–2 分钟',
    tags: ['草地', '坐姿', '柔美'],
    summary: '草地侧坐，裙摆铺开，画面柔软。',
    steps: [
      '铺垫布后屈膝侧坐，孕肚朝镜头一侧',
      '一手轻撑身后，一手托肚',
      '目光柔和下视或看镜头',
    ],
    tips: ['一定垫布防脏防潮', '起身慢', '孕晚期可改成长椅'],
    camera: '略俯拍，突出裙摆',
  }),
  pose({
    id: 'outdoor-path-walk',
    name: '步道慢走',
    scenes: ['outdoor', 'bamboo'],
    couple: false,
    duration: '2 分钟',
    tags: ['行走', '自然', '动态'],
    summary: '林荫或园路慢走，抓拍动态裙摆。',
    steps: [
      '沿步道斜向镜头方向慢走',
      '一手自然摆动或轻扶肚，肩放松',
      '偶尔回眸或看向两侧风景',
    ],
    tips: ['连拍选步伐稳的一帧', '地面平整防绊', '风大固定发丝'],
    camera: '全身，快门略提高',
  }),
  pose({
    id: 'bench-side-sit',
    name: '长椅侧坐',
    scenes: ['bench'],
    couple: false,
    duration: '2 分钟',
    tags: ['长椅', '坐姿', '公园'],
    summary: '公园长椅侧坐托肚，日常出片率最高。',
    steps: [
      '侧坐长椅一端，双脚自然落地',
      '一手托肚，一手搭椅背或扶手',
      '头微侧，目光看镜头或看向远方',
    ],
    tips: ['选干净无涂鸦的椅子', '背后背景尽量简洁', '起身扶稳'],
    camera: '半身或全身，横竖皆可',
  }),
  pose({
    id: 'bench-couple',
    name: '双人同椅',
    scenes: ['bench', 'couple'],
    couple: true,
    duration: '2 分钟',
    tags: ['长椅', '双人', '甜蜜'],
    summary: '同坐长椅，他环住她，公园双人经典。',
    steps: [
      '她侧坐或微侧，他坐在身侧贴近',
      '他一手环肩或托肚，她可靠他肩',
      '两人看同一方向，或轻声交谈',
    ],
    tips: ['两人身高对齐可让他略前倾', '注意椅面宽度', '抓自然互动'],
    camera: '半身横构图',
  }),
  pose({
    id: 'bench-glance',
    name: '长椅回眸',
    scenes: ['bench'],
    couple: false,
    difficulty: 2,
    tags: ['长椅', '回眸', '情绪'],
    summary: '坐着回眸看镜头，轻松又有故事感。',
    steps: [
      '面向长椅前方坐下，身体可微侧',
      '一手扶椅背，一手托肚',
      '头部回转看镜头，肩放松不要耸起',
    ],
    tips: ['脖子不要拧太狠', '背景留一条透视线', '发丝可拨到一侧'],
    camera: '半身，焦点在眼睛',
  }),
  pose({
    id: 'lake-rail',
    name: '湖栏侧影',
    scenes: ['lake'],
    couple: false,
    tags: ['湖边', '栏杆', '侧影'],
    summary: '手扶栏杆侧身，水面与天空做简洁背景。',
    steps: [
      '侧身站在栏杆旁，双手或一手轻扶栏',
      '另一手可托肚，肚弧朝向镜头',
      '目光看向水面远方或看镜头',
    ],
    tips: ['注意栏杆稳固与安全', '风大固定裙与发', '可利用水面反光'],
    camera: '全身或 3/4，横构图留出湖面',
  }),
  pose({
    id: 'lake-gaze-water',
    name: '望向水面',
    scenes: ['lake'],
    couple: false,
    tags: ['湖边', '沉思', '情绪'],
    summary: '静静望向水面，情绪留白。',
    steps: [
      '面向水面微侧站立，重心稳定',
      '双手交叠托肚或一手扶栏',
      '下巴微收，目光落在水面上某处',
    ],
    tips: ['傍晚侧逆光很美', '避免冲着太阳猛眯眼', '可拍剪影版本'],
    camera: '侧脸半身或全身',
  }),
  pose({
    id: 'lake-couple',
    name: '并肩望湖',
    scenes: ['lake', 'couple'],
    couple: true,
    duration: '1–2 分钟',
    tags: ['湖边', '双人', '浪漫'],
    summary: '两人并肩望湖，他轻环她的肩或腰。',
    steps: [
      '并肩面向湖面，她可靠他肩',
      '他一手环肩或托肚侧，她一手轻放他手臂',
      '可同时看远方，或她回眸看他',
    ],
    tips: ['对齐两人视线高度', '注意风吹发丝', '连拍抓回眸瞬间'],
    camera: '半身横构图，留出湖面',
  }),
  pose({
    id: 'street-brick',
    name: '砖墙侧身',
    scenes: ['street', 'town'],
    couple: false,
    tags: ['街边', '砖墙', '侧身'],
    summary: '浅色砖墙前经典侧影，城市感干净。',
    steps: [
      '侧身站在干净墙面前约半步',
      '一手托肚，另一手自然垂落或轻触墙面',
      '目光看镜头斜前方',
    ],
    tips: ['避开涂鸦与广告', '墙面纹理别太花', '注意路人入镜'],
    camera: '全身或半身，竖构图',
  }),
  pose({
    id: 'street-hand-walk',
    name: '街边牵手慢走',
    scenes: ['street', 'town', 'couple'],
    couple: true,
    duration: '2 分钟',
    tags: ['街边', '双人', '行走'],
    summary: '人少巷口牵手慢走，生活感双人照。',
    steps: [
      '选安静街段，并排斜向镜头慢走',
      '内侧手相牵，步伐放慢',
      '边走边轻声说话，可偶尔对视',
    ],
    tips: ['避开车流高峰', '连拍选步伐稳的一帧', '注意地面坑洼'],
    camera: '全身，快门略提高',
  }),
  pose({
    id: 'street-steps',
    name: '台阶轻坐',
    scenes: ['street', 'outdoor'],
    couple: false,
    difficulty: 2,
    duration: '1–2 分钟',
    tags: ['台阶', '坐姿', '城市'],
    summary: '低台阶侧坐，城市背景简洁有层次。',
    steps: [
      '选干净低矮台阶侧坐，一脚可踏下一级',
      '一手托肚，一手轻撑身侧',
      '抬头看镜头或看向街道远方',
    ],
    tips: ['台阶要干爽防滑', '起身扶稳', '背景尽量少路人'],
    camera: '半身或全身，略俯或平视',
  }),
  pose({
    id: 'closeup-hands-together',
    name: '牵手叠肚',
    scenes: ['closeup', 'couple'],
    couple: true,
    tags: ['特写', '双手', '双人'],
    summary: '两人的手交叠在孕肚上，温情特写。',
    steps: [
      '她的手先轻放肚上，他的手再叠上来',
      '手指自然舒展，可露出婚戒',
      '上身微前倾，让手与肚充满画面',
    ],
    tips: ['两人指甲干净', '手温差可先搓热', '焦点在上层手指'],
    camera: '特写，浅景深',
  }),
  pose({
    id: 'closeup-ring',
    name: '婚戒特写',
    scenes: ['closeup'],
    couple: false,
    tags: ['特写', '戒指', '细节'],
    summary: '手托肚时突出婚戒，故事感细节。',
    steps: [
      '托肚的手略侧向镜头，戒指朝外',
      '另一手可轻扶手腕或肚侧',
      '保持呼吸平稳，避免手抖',
    ],
    tips: ['戒指擦亮', '光线从侧前方打到金属反光', '可微距模式'],
    camera: '近景特写，焦点在戒指',
  }),

  // ——— 城市高频：咖啡 / 楼梯 / 花墙 / 阳台 / 镜面 + 补厚长椅窗边 ———
  pose({
    id: 'cafe-window-sit',
    name: '咖啡馆窗座',
    scenes: ['cafe', 'window'],
    couple: false,
    duration: '2 分钟',
    easy: true,
    tags: ['咖啡', '窗座', '坐姿'],
    summary: '靠窗而坐，日常最好拍的城市光影。',
    steps: [
      '选靠窗座位，身体微侧向窗户光线',
      '一手托肚，另一手可轻扶杯沿或放在桌面',
      '目光看镜头、看窗外或看向杯沿',
    ],
    tips: ['杯子做前景虚化很加分', '避开杂乱菜单与电线', '人少时段拍更从容'],
    camera: '半身横构图，侧光为主',
  }),
  pose({
    id: 'stairs-rail',
    name: '楼梯扶栏侧身',
    scenes: ['stairs', 'street'],
    couple: false,
    difficulty: 2,
    tags: ['楼梯', '扶栏', '侧身'],
    summary: '扶栏侧身站在台阶上，线条干净有层次。',
    steps: [
      '站在中段台阶，一手轻扶栏杆，身体微侧',
      '另一手托肚，重心放在稳定的那只脚',
      '抬头看镜头或沿楼梯方向望去',
    ],
    tips: ['选低缓楼梯，注意防滑', '不要站最边缘', '旁人在下方保护更好'],
    camera: '全身或 3/4，可略俯拍显层次',
  }),
  pose({
    id: 'flower-wall',
    name: '花墙半身',
    scenes: ['flower', 'outdoor'],
    couple: false,
    tags: ['花墙', '半身', '氛围'],
    summary: '花墙或绿植墙前半身托肚，季节感强。',
    steps: [
      '站在花墙前约半步，身体微侧或正面',
      '双手轻托孕肚，肩放松',
      '目光看镜头，表情自然轻笑',
    ],
    tips: ['花别挡脸', '注意花粉过敏', '人少时拍，避免路人抢镜'],
    camera: '半身，浅景深虚化花墙',
  }),
  pose({
    id: 'balcony-sunset',
    name: '阳台日落',
    scenes: ['balcony', 'outdoor'],
    couple: false,
    difficulty: 2,
    duration: '1–2 分钟',
    tags: ['阳台', '日落', '逆光'],
    summary: '阳台看向落日，逆光勾轮廓，氛围出彩。',
    steps: [
      '侧身或微侧站在阳台护栏内侧安全处',
      '一手轻扶护栏，一手托肚',
      '脸可迎向余晖，或看向远方留白',
    ],
    tips: ['务必注意护栏安全', '逆光时给脸补一点柔光', '日落前后半小时最佳'],
    camera: '半身或全身，可尝试剪影',
  }),
  pose({
    id: 'mirror-side',
    name: '镜面侧影',
    scenes: ['mirror', 'indoor'],
    couple: false,
    tags: ['镜子', '侧影', '全身'],
    summary: '全身镜前侧身，一次拍出线条与空间感。',
    steps: [
      '侧身对镜约 70°–90°，肚弧朝向镜子可见',
      '一手托肚，另一手自然垂落',
      '看向镜中自己或看向镜头（镜头可避开入镜）',
    ],
    tips: ['先擦净镜面', '摄影师可蹲侧面避免入镜', '背景简洁更好'],
    camera: '拍镜中影像或实人+镜中双重轮廓',
  }),
  pose({
    id: 'bench-stretch',
    name: '长椅闲坐伸腿',
    scenes: ['bench'],
    couple: false,
    duration: '2 分钟',
    easy: true,
    tags: ['长椅', '闲坐', '轻松'],
    summary: '长椅上放松伸腿，最适合没事随手拍。',
    steps: [
      '坐满长椅，双腿自然向前或交叠脚踝',
      '一手托肚，一手搭椅背',
      '头微靠后或看向远方，表情放松',
    ],
    tips: ['比正襟危坐更自然', '注意裙摆与座位干净', '起身慢一点'],
    camera: '半身横构图，略低于视线',
  }),
  pose({
    id: 'window-soft-hold',
    name: '窗边柔光托肚',
    scenes: ['window'],
    couple: false,
    easy: true,
    tags: ['窗光', '托肚', '半身'],
    summary: '窗边正面微侧托肚，补厚窗边系列的日常款。',
    steps: [
      '面向窗户微侧 20°，让柔光打在脸上与孕肚',
      '双手交叠轻托肚前',
      '目光看镜头或看向窗外光亮处',
    ],
    tips: ['比剪影更好拍、更稳', '纱帘可减弱硬光', '露出锁骨更显精神'],
    camera: '半身，曝光参考脸部',
  }),
  pose({
    id: 'stairs-low-sit',
    name: '低阶轻坐',
    scenes: ['stairs', 'street'],
    couple: false,
    difficulty: 2,
    duration: '1–2 分钟',
    easy: true,
    tags: ['台阶', '坐姿', '轻松'],
    summary: '坐在低矮台阶，城市背景有层次又省力。',
    steps: [
      '选干净干爽的低阶侧坐，一脚可踏下一级',
      '一手托肚，一手轻撑身侧',
      '抬头看镜头或看向街道远方',
    ],
    tips: ['台阶要防滑', '起身扶稳', '比高楼梯安全得多'],
    camera: '半身或全身，平视或略俯',
  }),

  // ——— 全身人像：焦点在人，孕肚自然存在不刻意托举 ———
  pose({
    id: 'fullbody-front-smile',
    name: '正面全身轻笑',
    scenes: ['outdoor', 'indoor'],
    couple: false,
    easy: true,
    tags: ['全身', '正面', '人像'],
    summary: '正对镜头全身站立，焦点在脸与气质，孕肚自然即可。',
    steps: [
      '正面站立，双脚与肩同宽，膝盖放松，肩沉下去',
      '双手自然垂在身侧或轻交叠于身前（不要托肚）',
      '看镜头轻笑，下巴微收，像在拍普通人像',
    ],
    tips: ['先对焦眼睛', '衣服整洁比摆肚更重要', '全身留头脚空间'],
    camera: '全身竖构图，平视或略低，焦点在眼睛',
  }),
  pose({
    id: 'fullbody-walk-toward',
    name: '迎面缓步',
    scenes: ['outdoor', 'street'],
    couple: false,
    difficulty: 2,
    duration: '1–2 分钟',
    tags: ['全身', '步行', '自然'],
    summary: '朝镜头缓步走来，动态全身，捕捉表情与步伐。',
    steps: [
      '面向镜头慢慢走，步幅小、脚跟着地稳',
      '双臂自然摆动，不要特意摸肚子',
      '偶尔看镜头轻笑，或看向前方',
    ],
    tips: ['连拍选步伐稳的一帧', '地面平整防绊', '风大时按住裙摆一侧即可'],
    camera: '全身，快门略提高，对焦预判前移',
  }),
  pose({
    id: 'fullbody-coat-drape',
    name: '披外套站立',
    scenes: ['street', 'outdoor'],
    couple: false,
    tags: ['全身', '外套', '街拍'],
    summary: '外套披肩或搭臂，全身街拍感，人是主角。',
    steps: [
      '微侧 20° 站立，外套披在肩上或挂在一臂',
      '一手插袋或扶外套领，另一手自然垂落',
      '目光看镜头或斜前方，表情放松',
    ],
    tips: ['外套颜色与裙装对比更好看', '不要把外套裹紧勒肚', '背景选干净墙面'],
    camera: '全身竖构图，略留环境',
  }),
  pose({
    id: 'fullbody-look-aside',
    name: '全身侧望',
    scenes: ['outdoor', 'lake'],
    couple: false,
    tags: ['全身', '侧望', '情绪'],
    summary: '身体微侧、目光望向远方，情绪与线条并重。',
    steps: [
      '身体侧向镜头约 45°，重心在后脚',
      '双手自然垂落或一手轻扶衣摆，远离孕肚',
      '头转向远方或微回眸，眼神柔和',
    ],
    tips: ['发丝可拨到一侧', '背景简洁留白', '比托肚侧影更像人像'],
    camera: '全身，可略侧光勾轮廓',
  }),
  pose({
    id: 'fullbody-dress-flow',
    name: '裙摆轻扬',
    scenes: ['outdoor', 'flower', 'bamboo'],
    couple: false,
    difficulty: 2,
    duration: '1–2 分钟',
    tags: ['全身', '裙摆', '动感'],
    summary: '轻转或拎裙摆，衣服流动感强，脸仍是视觉中心。',
    steps: [
      '微侧站立，一手轻拎裙摆向外扬起一点',
      '另一手自然垂落或轻扶腰侧（勿托肚）',
      '头看镜头或回眸，趁裙摆动时连拍',
    ],
    tips: ['动作要小、慢', '注意脚下防绊', '选有微风的户外更好'],
    camera: '全身，快门略提高冻住裙摆',
  }),
  pose({
    id: 'indoor-hall-walk',
    name: '走廊缓步',
    scenes: ['indoor', 'mirror'],
    couple: false,
    tags: ['全身', '走廊', '室内'],
    summary: '酒店/家中走廊迎面走来，空间纵深衬全身。',
    steps: [
      '沿走廊中线朝镜头慢慢走',
      '手臂自然，表情看镜头或看向尽头光',
      '肩放松，步伐稳，像日常回家',
    ],
    tips: ['选光线均匀的走廊', '地面防滑', '尽头有窗户光更出片'],
    camera: '全身居中，利用走廊透视',
  }),
  pose({
    id: 'outdoor-open-stand',
    name: '空地站立',
    scenes: ['outdoor'],
    couple: false,
    easy: true,
    tags: ['全身', '站立', '简洁'],
    summary: '开阔地正面/微侧站立，干净背景突出人。',
    steps: [
      '选背景简洁的草地或空地，正面或微侧 15°',
      '双手自然垂落，或轻握身前（不托肚）',
      '看镜头自然微笑，呼吸放缓',
    ],
    tips: ['避开杂乱树干挡身', '裙摆整理平整', '黄金时段柔光更好'],
    camera: '全身，人物居中略偏，焦点在脸',
  }),
  pose({
    id: 'window-full-portrait',
    name: '窗前全身',
    scenes: ['window', 'indoor'],
    couple: false,
    tags: ['全身', '窗光', '人像'],
    summary: '窗边全身站立，侧光打在脸与身形上。',
    steps: [
      '侧对窗户约 30°–45°，让柔光打在脸上',
      '双手自然垂落或一手轻扶窗框，另一手自然',
      '目光看镜头或看向窗外，神情安静',
    ],
    tips: ['脸部曝光优先', '纱帘可柔化硬光', '全身别裁掉脚'],
    camera: '全身竖构图，曝光参考脸部',
  }),

  // ——— 浙北慢游 / 孕晚短拍补充 ———
  pose({
    id: 'bamboo-side-stand',
    name: '竹林侧影',
    scenes: ['bamboo', 'outdoor'],
    couple: false,
    tags: ['竹林', '侧身', '车观停拍'],
    summary: '竹浪夹道边侧身定格，适合鄣吴镇周与省道起步段。',
    steps: [
      '车停安全路肩后下车，侧身对镜头约 70°',
      '一手轻托肚底或自然垂落，背景留给竹林纵深',
      '目光看斜前方，肩放松，拍 3–5 张即回车',
    ],
    tips: ['会车时先回车再拍', '湿滑土路少走', '仰拍竹梢透光更有层次'],
    camera: '全身或 3/4，略仰角度',
    image: 'poses/outdoor-tree.png',
    svg: 'outdoor-tree',
  }),
  pose({
    id: 'bamboo-path-slow',
    name: '竹径慢走',
    scenes: ['bamboo', 'outdoor'],
    couple: false,
    duration: '1–2 分钟',
    tags: ['竹林', '慢走', '全身'],
    summary: '沿平坦竹径迎面缓步，裙摆与竹浪一起动。',
    steps: [
      '选平坦路段朝镜头慢慢走，步幅小',
      '手臂自然摆动，表情看镜头或看竹梢',
      '走 6–8 步连拍，不要跑',
    ],
    tips: ['路窄时他走在外侧护着', '地面落叶湿滑要扶', '国庆避开景区深处大路'],
    camera: '全身居中，快门略提高',
    image: 'poses/outdoor-path-walk.png',
    svg: 'outdoor-path-walk',
  }),
  pose({
    id: 'bamboo-couple-frame',
    name: '竹间环抱',
    scenes: ['bamboo', 'couple', 'outdoor'],
    couple: true,
    duration: '1–2 分钟',
    tags: ['竹林', '双人', '环抱'],
    summary: '竹林作框，他从身后轻环，安静有呼吸感。',
    steps: [
      '女主微侧，男主站身后半步，双手轻环孕肚上方（不按压）',
      '两人看同一方向或男主看她侧脸',
      '拍完立刻回车休息',
    ],
    tips: ['身高差可屈膝对齐', '背景留竹竿竖线', '雨天只找有遮蔽的路边'],
    camera: '半身到全身，焦点在女主眼睛',
    image: 'poses/couple-behind.png',
    svg: 'couple-behind',
  }),
  pose({
    id: 'town-white-wall',
    name: '白墙侧影',
    scenes: ['town', 'street'],
    couple: false,
    tags: ['古镇', '白墙', '侧身'],
    summary: '黛瓦白墙前经典侧影，适合鄣吴老街与新市巷口。',
    steps: [
      '选干净白墙、人少巷口，侧身 70°–90°',
      '一手轻托肚或扶墙，另一手自然',
      '目光柔和看镜头或巷尽头',
    ],
    tips: ['石板湿滑放慢脚步', '避开店招杂乱背景', '短拍即走，不必逛完镇'],
    camera: '半身或全身，留白墙负空间',
    image: 'poses/street-brick.png',
    svg: 'street-brick',
  }),
  pose({
    id: 'town-stone-walk',
    name: '石板巷慢走',
    scenes: ['town', 'street', 'couple'],
    couple: true,
    duration: '2 分钟',
    tags: ['古镇', '牵手', '慢走'],
    summary: '石板巷牵手慢走，生活感孕妇照。',
    steps: [
      '人少时段牵手朝镜头或背对缓走',
      '步伐小、有搀扶，表情自然交谈感',
      '连拍 8–10 张选一张有呼吸感的',
    ],
    tips: ['雨天慎走湿石板', '少站桥心人流', '他走在外侧'],
    camera: '全身跟随或定点连拍',
    image: 'poses/street-hand-walk.png',
    svg: 'street-hand-walk',
  }),
  pose({
    id: 'town-bridge-rail',
    name: '桥栏侧望',
    scenes: ['town', 'lake'],
    couple: false,
    tags: ['古桥', '栏杆', '水乡'],
    summary: '运河/溪桥栏杆侧望水面，适合新市太平桥一带短停。',
    steps: [
      '站桥侧平地或近岸栏杆内侧（勿探身）',
      '侧身望水面，一手轻扶栏，一手自然',
      '拍完立刻离开桥面人流',
    ],
    tips: ['人多时改拍岸边', '栏杆湿滑扶稳', '逆光记得补脸'],
    camera: '半身，水面作背景虚化',
    image: 'poses/lake-rail.png',
    svg: 'lake-rail',
  }),
  pose({
    id: 'rain-inn-window',
    name: '雨窗凝望',
    scenes: ['window', 'indoor'],
    couple: false,
    tags: ['雨天', '民宿', '窗光'],
    summary: '鄣吴雨日窗边：听雨看竹，情绪留白的室内孕照。',
    steps: [
      '侧对窗约 30°，柔光打在脸上',
      '一手轻扶窗框或托肚，目光望向窗外雨丝',
      '肩放松，像在歇口气',
    ],
    tips: ['Day3 大雨主拍姿', '纱帘可柔化硬光', '室内温度舒服再拍'],
    camera: '半身竖构图，曝光参考脸',
    image: 'poses/window-gaze.png',
    svg: 'window-gaze',
  }),
  pose({
    id: 'rain-couple-window',
    name: '雨窗依偎',
    scenes: ['window', 'couple', 'indoor'],
    couple: true,
    duration: '1–2 分钟',
    tags: ['雨天', '双人', '窗边'],
    summary: '窗边他从侧后轻靠，适合雨天民宿不想出门时。',
    steps: [
      '女主靠窗，男主站侧后，一手轻扶她肩或孕肚上方',
      '两人看窗外或她看镜头他看她',
      '拍 4–6 张即休息',
    ],
    tips: ['手不要按压孕肚', '窗外雨景虚化更有故事', '室温与穿衣先舒服'],
    camera: '半身，焦点在女主眼睛',
    image: 'poses/couple-behind.png',
    svg: 'couple-behind',
  }),
  pose({
    id: 'lake-dock-stand',
    name: '码头平地站',
    scenes: ['lake', 'outdoor'],
    couple: false,
    easy: true,
    tags: ['太湖', '码头', '全身'],
    summary: '渔人码头/月亮广场平地站立，湖面做干净背景。',
    steps: [
      '只站正规铺装平地，背向或侧向开阔湖面',
      '正面微侧 15°，双手自然或轻握身前',
      '看镜头轻笑，拍完回车',
    ],
    tips: ['湿滑石滩不下', '风大注意发丝与外套', '国庆人多就换路段少停'],
    camera: '全身，湖面留白',
    image: 'poses/outdoor-open-stand.png',
    svg: 'outdoor-open-stand',
  }),
  pose({
    id: 'lake-couple-walk',
    name: '湖岸并肩走',
    scenes: ['lake', 'couple', 'outdoor'],
    couple: true,
    duration: '2 分钟',
    tags: ['太湖', '双人', '慢走'],
    summary: '滨湖观景带正规步道上并肩慢走。',
    steps: [
      '沿观景步道朝镜头或平行慢走',
      '牵手或他轻扶肘，步幅小',
      '表情交谈感，连拍选自然一张',
    ],
    tips: ['不走临水无护栏区', '人流大时缩短路程', '结束后回车歇腿'],
    camera: '全身跟随或定点',
    image: 'poses/couple-walk.png',
    svg: 'couple-walk',
  }),
  pose({
    id: 'car-scenic-frame',
    name: '车窗框景',
    scenes: ['outdoor', 'bamboo', 'lake'],
    couple: false,
    easy: true,
    tags: ['车窗', '少下车', '省道'],
    summary: '不下车也能拍：车窗框住湖光或竹浪，孕晚期最省力。',
    steps: [
      '车停安全处，摇下副驾或后窗',
      '侧身面向窗外，一手可轻扶窗框',
      '镜头在车外或后座拍，用窗框做前景',
    ],
    tips: ['危险路段绝不停车', '引擎可关减震动', '风大关小窗'],
    camera: '半身，窗框入镜',
    image: 'poses/window-full-portrait.png',
    svg: 'window-full-portrait',
  }),
];

/** 短拍快选：适合没事随手拍几张，不是一天拍完 */
export const SHORT_PACKS = [
  {
    id: 'easy3',
    label: '随便拍 3 张',
    desc: '最轻松 · 约 10 分钟',
    ids: ['indoor-sofa-hold', 'bench-side-sit', 'closeup-hands'],
  },
  {
    id: 'home',
    label: '在家随拍',
    desc: '窗边 + 沙发 + 特写',
    ids: ['window-gaze', 'indoor-sofa-hold', 'closeup-profile-face', 'seated-chair'],
  },
  {
    id: 'outing',
    label: '出门顺路',
    desc: '咖啡 / 楼梯 / 街边',
    ids: ['cafe-window-sit', 'stairs-rail', 'street-brick', 'mirror-side'],
  },
  {
    id: 'park',
    label: '公园散步',
    desc: '长椅 + 花墙 + 树下',
    ids: ['bench-side-sit', 'bench-stretch', 'flower-wall', 'outdoor-tree'],
  },
  {
    id: 'portrait',
    label: '全身人像',
    desc: '焦点在人 · 不刻意托肚',
    ids: [
      'fullbody-front-smile',
      'fullbody-walk-toward',
      'fullbody-coat-drape',
      'window-full-portrait',
    ],
  },
  {
    id: 'trip-binhu',
    label: '滨湖大道短拍',
    desc: '太湖 · Day1 · 约 20 分钟',
    ids: [
      'lake-dock-stand',
      'lake-rail',
      'lake-gaze-water',
      'lake-couple-walk',
      'lake-couple',
      'car-scenic-frame',
    ],
  },
  {
    id: 'trip-zhangwu',
    label: '鄣吴雨日 / 老街',
    desc: '连住两晚 · 室内+短走',
    ids: [
      'rain-inn-window',
      'rain-couple-window',
      'town-white-wall',
      'town-stone-walk',
      'solo-lean-wall',
      'closeup-hands',
    ],
  },
  {
    id: 'trip-bamboo',
    label: '竹林 + 省道',
    desc: 'Day4 公路高潮 · 少下车',
    ids: [
      'bamboo-side-stand',
      'bamboo-path-slow',
      'bamboo-couple-frame',
      'car-scenic-frame',
      'fullbody-dress-flow',
      'outdoor-open-stand',
    ],
  },
  {
    id: 'trip-canal',
    label: '运河夕光（可选）',
    desc: '新市短停 · 累了可跳过',
    ids: [
      'town-bridge-rail',
      'town-white-wall',
      'town-stone-walk',
      'lake-gaze-water',
      'couple-walk',
    ],
  },
];

/** 拍摄模式默认队列：等同「随便拍 3 张」再补两张经典 */
export const SUGGESTED_EIGHT = [
  'indoor-sofa-hold',
  'bench-side-sit',
  'cafe-window-sit',
  'window-gaze',
  'couple-behind',
  'closeup-hands',
];

export const STATS = {
  poseCount: POSES.length,
  sceneCount: CATEGORIES.filter((c) => c.id !== 'all').length,
};
